import React, { useEffect, useState } from "react"
import { Col, Div, Row, Text } from "atomize"
import { motion } from "framer-motion"
import { DateTime } from "luxon"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCheck } from "@fortawesome/free-solid-svg-icons"
import Container from "../atoms/Container"
import SecondaryButton from "../buttons/SecondaryButton"
import { theme } from "../settings/config"
import { MASTER, VERSIONS_PATH, versionSubtitle } from "../devApp/versions"
import { currentDevVersion, switchDevVersion } from "../devApp/devApp"

const relative = at => DateTime.fromMillis(at).setLocale("it").toRelative()

// L'app dev: da quale versione la stai provando. Si apre toccando la
// pastiglia in alto (EnvironmentBadge), e c'è solo nell'app dev.
export function DevVersionsModal({ closeModal }) {
	const current = currentDevVersion() ?? MASTER
	const [versions, setVersions] = useState(null)
	const [failed, setFailed] = useState(false)
	const [opening, setOpening] = useState(null)

	useEffect(() => {
		let cancelled = false
		fetch(VERSIONS_PATH, { cache: "no-store" })
			.then(res => (res.ok ? res.json() : Promise.reject(new Error(res.status))))
			.then(body => !cancelled && setVersions(body.versions))
			.catch(() => !cancelled && setFailed(true))
		return () => {
			cancelled = true
		}
	}, [])

	const choose = id => {
		if (opening) return
		if (id === current) return closeModal()
		setOpening(id)
		switchDevVersion(id)
	}

	const sectionTitle = text => (
		<Text
			textSize="caption"
			textWeight="bold"
			textColor={theme.colors.text_light}
			m={{ t: "1rem", b: "0.5rem" }}
			style={{ textTransform: "uppercase", letterSpacing: "0.04em" }}>
			{text}
		</Text>
	)

	const block = ({ id, title, subtitle, disabled }) => {
		const isCurrent = id === current
		return (
			<motion.button
				key={id}
				type="button"
				whileTap={disabled ? undefined : { scale: 0.98 }}
				disabled={disabled || (opening !== null && opening !== id)}
				onClick={() => choose(id)}
				data-testid={`dev-version-${id}`}
				style={{
					display: "flex",
					alignItems: "center",
					gap: "0.75rem",
					width: "100%",
					padding: "0.8rem 1rem",
					border: 0,
					borderRadius: "15px",
					background: theme.colors.surface,
					textAlign: "left",
					fontFamily: "inherit",
					cursor: disabled ? "default" : "pointer",
					opacity: disabled ? 0.55 : 1,
				}}>
				<Div style={{ flex: 1, minWidth: 0 }}>
					<Text
						textColor={theme.colors.gunmetal}
						textWeight={isCurrent ? "bold" : "500"}
						style={{
							fontSize: "0.9375rem",
							lineHeight: 1.3,
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							overflow: "hidden",
						}}>
						{title}
					</Text>
					<Text textSize="caption" textColor={theme.colors.text_light} m={{ t: "0.15rem" }}>
						{opening === id ? "Apro…" : subtitle}
					</Text>
				</Div>
				{isCurrent && (
					<Div
						flexShrink="0"
						w="24px"
						h="24px"
						rounded="circle"
						bg={theme.colors.primary}
						d="flex"
						align="center"
						justify="center">
						<FontAwesomeIcon icon={faCheck} color={theme.colors.white} fontSize="0.75rem" />
					</Div>
				)}
			</motion.button>
		)
	}

	return (
		<>
			<Container>
				<Div m={{ b: "0.5rem" }} textWeight="bold" textSize="title">
					Quale versione provi?
				</Div>
			</Container>
			<Div maxH="60vh" style={{ overflowY: "auto", overflowX: "hidden" }}>
				<Container>
					<Text textColor={theme.colors.text_light}>
						Sempre sui dati di prova: quello che fai qui non tocca nessun circolo vero.
					</Text>

					{sectionTitle("La base")}
					{block({ id: MASTER, title: "master", subtitle: "com'è adesso l'app vera" })}

					{sectionTitle("Le PR aperte")}
					{failed ? (
						<Text textColor={theme.colors.text_light}>
							Non riesco a leggere le PR aperte: controlla la rete e riapri.
						</Text>
					) : !versions ? (
						<Text textColor={theme.colors.text_light}>Cerco le PR aperte…</Text>
					) : versions.length === 0 ? (
						<Text textColor={theme.colors.text_light}>Nessuna PR aperta.</Text>
					) : (
						<Div d="flex" flexDir="column" style={{ gap: "0.5rem" }}>
							{versions.map(version =>
								block({
									id: String(version.pr),
									title: version.title,
									subtitle: versionSubtitle(version, relative),
									disabled: !version.url,
								}),
							)}
						</Div>
					)}
				</Container>
			</Div>
			<Div m={{ x: "1rem" }}>
				<Row m={{ t: "1rem" }}>
					<Col p="0">
						<SecondaryButton
							m="auto"
							textColor={theme.colors.gunmetal}
							textSize="subheader"
							text="Chiudi"
							action={closeModal}
						/>
					</Col>
				</Row>
			</Div>
		</>
	)
}

export default DevVersionsModal
